import React from 'react';
import { get as _get } from 'lodash';

import { toJS } from 'mobx';
import { getStores } from '../stores';
import modalAPI from './modal';
import {
  setIntegrationData,
  deleteIntegrationData,
} from './embedded-integrations';
import { openPopup } from '../utils/dom';
import { get } from '../utils/data';
import FlowModalLayout from '../components/modals/flow-modal-layout';
import NotificationModalLayout from '../components/modals/notification-modal-layout';
import Footnote from '../components/modals/footnote';
import DeleteConfirmationModal from '../components/modals/delete-confirmation';
import UpdateConfirmationModal from '../components/modals/update-confirmation';
import DomainUpgrade from '../components/modals/domain-upgrade';
import GenericError from '../components/modals/generic-error';

export const integrationStatus = {
  NEW: 'new',
  DISCONNECTED: 'disconnected',
  CONNECTED: 'connected',
  LOGGEDIN: 'logged-in',
};

const api = (key, config, { publish, TOPICS }) => {
  const apiGet = get(config);

  const openErrorModal = (biData) => {
    publish(TOPICS.SHOW_MODAL_DIALOG, <GenericError biData={biData} />);
  };

  return {
    openPopup: (...args) => openPopup(...args),

    openFlowModal: ({
      params,
      onClosed: userOnClosed,
      onOpened: userOnOpened,
      contentNode,
      title,
      subTitleNode,
      buttons,
      footnoteNode,
    }) => {
      params && getStores().view.setParams(params);

      const baseProps = {
        roundBottomCorners: !footnoteNode,
        title,
        subTitleNode,
        contentNode,
        onXClick: () => {
          _get(buttons, 'x.onClick', () => {})(modalAPI);
          modalAPI.close();
        },
        primaryButtonLabel: buttons.primary.text,
        primaryButtonHeight: buttons.primary.height,
        onPrimaryButtonClick: () => buttons.primary.onClick(modalAPI),
      };

      userOnOpened && (baseProps.onOpened = () => userOnOpened(modalAPI));
      userOnClosed && (baseProps.onClosed = () => userOnClosed(modalAPI));

      const additionalProps = !buttons.secondary
        ? {
            footerSize: 'small',
          }
        : {
            footerSize: 'large',
            secondaryButtonLabel: buttons.secondary.text,
            onSecondaryButtonClick: () => buttons.secondary.onClick(modalAPI),
          };

      publish(
        TOPICS.SHOW_MODAL_DIALOG,
        <div>
          <FlowModalLayout {...baseProps} {...additionalProps} />
          {footnoteNode && <Footnote>{footnoteNode}</Footnote>}
        </div>,
      );
    },

    openNotificationModal: ({
      illustration,
      title,
      subTitle,
      buttons,
      onOpened,
      onClosed,
    }) =>
      publish(
        TOPICS.SHOW_MODAL_DIALOG,
        <NotificationModalLayout
          title={title}
          subTitle={subTitle}
          illustration={illustration}
          primaryButtonLabel={buttons.primary.text}
          primaryButtonHeight={buttons.primary.height}
          onPrimaryButtonClick={() => buttons.primary.onClick(modalAPI)}
          secondaryButtonLabel={buttons.secondary && buttons.secondary.text}
          onSecondaryButtonClick={() =>
            buttons.secondary && buttons.secondary.onClick(modalAPI)
          }
          onXClick={() => {
            _get(buttons, 'x.onClick', () => {})(modalAPI);
            modalAPI.close();
          }}
          footerSize="small"
          onOpened={onOpened && (() => onOpened(modalAPI))}
          onClosed={onClosed && (() => onClosed(modalAPI))}
        />,
      ),

    openDeleteConfirmationModal: ({ onDelete, onCancel }) => {
      publish(
        TOPICS.SHOW_MODAL_DIALOG,
        <DeleteConfirmationModal
          onClose={() => modalAPI.close()}
          onDelete={onDelete && (() => onDelete(modalAPI))}
          onCancel={onCancel && (() => onCancel(modalAPI))}
        />,
      );
    },

    openUpdateConfirmationModal: ({ onUpdate, onCancel }) => {
      publish(
        TOPICS.SHOW_MODAL_DIALOG,
        <UpdateConfirmationModal
          onClose={() => modalAPI.close()}
          onUpdate={onUpdate && (() => onUpdate(modalAPI))}
          onCancel={onCancel && (() => onCancel(modalAPI))}
        />,
      );
    },

    openDomainUpgradeModal: (biData) => {
      publish(TOPICS.SHOW_MODAL_DIALOG, <DomainUpgrade biData={biData} />);
    },

    openErrorModal,

    getIntegrationInstance: () =>
      apiGet({ path: `instance?integration=${key}` }),

    installTpa: () => apiGet({ path: `install-tpa?integration=${key}` }),

    openTrackAndAnalyzeLink() {
      const metaSiteId = config.metaSiteId;
      const trackAndAnalyzeLink = `https://www.wix.com/dashboard/${metaSiteId}/manage-website/embeds/`;
      const ref = window.open(trackAndAnalyzeLink, '_blank');
      if (ref) {
        ref.focus();
      }
    },

    getMetaSiteId: () => config.metaSiteId,

    setIntegrationProperty: (_key, property, status) =>
      getStores().integrations.setProperty(_key, property, status),

    embeddedIntegrations: {
      isDomainConnected: () =>
        getStores().embeddedIntegrations.isDomainConnected,
      isWixSite: () => getStores().embeddedIntegrations.isWixSite,
      get: (type) =>
        getStores().embeddedIntegrations.getEmbeddedIntegration(type),
      set: (type, integrationData) => {
        const { embeddedIntegrations: store, biLogger } = getStores();
        const biData = {
          integrationType: type,
        };
        biLogger.embeddedCodeSaveInitiate(biData);
        const siteId = store.getSiteId();
        return setIntegrationData(config, siteId, integrationData)
          .then(() => {
            biLogger.embeddedCodeSaveSuccess(biData);
            store.setEmbeddedIntegration(type, integrationData);
          })
          .catch((ex) => {
            biLogger.embeddedCodeSaveError({ ...biData, errorType: ex });
            openErrorModal(biData);
            console.error(
              `Error setting integration with type '${type}': ${ex}`,
            );
          });
      },
      createDraft: (integrationConfig) => {
        const store = getStores().embeddedIntegrations;
        let integrationData = store.getEmbeddedIntegration(
          integrationConfig.type,
        );
        if (!integrationData) {
          integrationData = store.generateEmbeddedIntegration(
            integrationConfig,
          );
        }
        modalAPI.setParams({ integrationData: toJS(integrationData) });
      },
      getStatus: (type) =>
        getStores().embeddedIntegrations.getIntegrationStatus(type),
      enableIntegration: (type, isEnabled) => {
        const { embeddedIntegrations: store } = getStores();
        const integrationData = store.getEmbeddedIntegration(type);

        const siteId = store.getSiteId();
        return setIntegrationData(config, siteId, {
          ...integrationData,
          enabled: isEnabled,
        })
          .then(() => {
            store.setEmbeddedIntegrationProp(type, 'enabled', isEnabled);
          })
          .catch((ex) => {
            const biData = {
              integrationType: type,
            };
            openErrorModal(biData);
            console.error(
              `Error enabling/disabling integration with type '${type}': ${ex}`,
            );
          });
      },
      delete: (type) => {
        const store = getStores().embeddedIntegrations;
        const siteId = store.getSiteId();
        const { id: embedId } = store.getEmbeddedIntegration(type);
        return deleteIntegrationData(config, siteId, embedId)
          .then(() => {
            store.deleteEmbeddedIntegration(type);
          })
          .catch((ex) => {
            const biData = {
              integrationType: type,
            };
            openErrorModal(biData);
            console.error(
              `Error deleting integration with type '${type}': ${ex}`,
            );
          });
      },
    },
  };
};

export default api;
